import { isStandardBusinessAccount } from '../../dataAccess/accountTypes'
import { createCheckoutSession } from '../../dataAccess/customers'
import { asRecordId } from '../../dataAccess/queries'

// TODO: gradually move all direct url firePost and fireGet usage to api.
const pathCreator = (prefix, version) => {
  const versionedPrefix = version ? `${version}/${prefix}` : prefix
  return (suffix = '') => `${versionedPrefix}/${suffix}`
}
export default class Api {
  #httpClient
  #functionsClient
  #createFormData

  constructor(httpClient, functionsClient, createFormData) {
    this.#httpClient = httpClient
    this.#functionsClient = functionsClient
    this.#createFormData = createFormData
  }
  #accountsPath = pathCreator('accounts')
  #usersPath = pathCreator('users')
  #clientInvitations = pathCreator('client-invitations')
  #documentsPath = pathCreator('documents', 'v2')
  #companyPath = pathCreator('share-register/company')

  // Accounts
  getAccountSignatories(account) {
    return this.#httpClient.get({
      path: this.#accountsPath(`${asRecordId(account)}/signatories`)
    })
  }

  // Share Register
  addShareHolding(data) {
    return this.#httpClient.post({
      path: this.#companyPath('holding'),
      data
    })
  }

  addShareSeries(data) {
    return this.#httpClient.post({
      path: this.#companyPath('series'),
      data
    })
  }

  closeShareSeries(seriesName) {
    return this.#httpClient.post({
      path: this.#companyPath(`series/close/${seriesName}`)
    })
  }

  addShareOption(data) {
    return this.#httpClient.post({
      path: this.#companyPath('share-option'),
      data
    })
  }

  deleteShareHolding(holding) {
    return this.#httpClient.delete({
      path: this.#companyPath(`holding/${holding.id}`)
    })
  }

  putShareHolding(data) {
    return this.#httpClient.put({
      path: this.#companyPath(`holding/${data.id}`),
      data
    })
  }

  // Documents
  deleteDocument(document) {
    return this.#httpClient.delete({
      path: `documents/${asRecordId(document)}`
    })
  }

  sendForSignature({ id }) {
    return this.#httpClient.post({
      path: this.#documentsPath(`${id}/send-for-signature`)
    })
  }

  createDocumentPreparationSession({ id }) {
    return this.#httpClient.post({
      path: this.#documentsPath(`${id}/create-preparation-session`)
    })
  }

  getSignatureEndpoint({ id }) {
    return this.#httpClient.get({
      path: this.#documentsPath(`${id}/signature-endpoint`)
    })
  }

  uploadNewDocumentVersion(id, file, fields) {
    return this.#httpClient.post({
      path: this.#documentsPath(`${id}/upload-new-version`),
      data: this.#createFormData(file, fields)
    })
  }

  uploadUnsignedDocument(file, fields) {
    return this.#httpClient.post({
      path: 'unsigned-document/upload',
      data: this.#createFormData(file, fields)
    })
  }

  uploadSignedDocument(file, fields) {
    return this.#httpClient.post({
      path: 'documents/upload',
      data: this.#createFormData(file, fields)
    })
  }

  // Client invitations
  inviteAsClient({ name, email }) {
    return this.#httpClient.post({
      path: this.#clientInvitations(),
      data: { email, name }
    })
  }

  acceptClientInvitation(clientInvitation) {
    return this.#httpClient.post({
      path: this.#clientInvitations(`${asRecordId(clientInvitation)}/accept`)
    })
  }

  declineClientInvitation(clientInvitation) {
    return this.#httpClient.post({
      path: this.#clientInvitations(`${asRecordId(clientInvitation)}/decline`)
    })
  }

  sendClientInvitationReminder(clientInvitation) {
    return this.#httpClient.post({
      path: this.#clientInvitations(
        `${asRecordId(clientInvitation)}/send-reminder`
      )
    })
  }

  // Users
  addCounterparty({ name, email, accountType, businessName }) {
    const baseData = { name, email, accountType }
    const data = isStandardBusinessAccount(accountType)
      ? { ...baseData, businessName }
      : baseData
    return this.#httpClient.post({
      path: this.#usersPath('counterparty'),
      data
    })
  }

  sentEmailVerification() {
    return this.#httpClient.post({
      path: this.#usersPath('sentEmailVerification')
    })
  }

  // Customers
  createPortalLink({ returnUrl }) {
    const createPortalLinkFunctionRef = this.#functionsClient.httpsCallable(
      'ext-firestore-stripe-payments-createPortalLink'
    )
    return createPortalLinkFunctionRef({ returnUrl })
  }

  async checkout({ user, checkoutData }) {
    const ref = await createCheckoutSession(user, checkoutData)
    return new Promise((resolve, reject) => {
      ref.onSnapshot((snap) => {
        const { error, url } = snap.data() || {}
        if (error) {
          reject(new Error(`Checkout#${snap.id} failed: ${error.message}`))
          return
        }
        if (url) {
          resolve({ url })
        }
      })
    })
  }
}
